import { useState } from 'react';
import { prepareFileListForMedia } from '../../../utils/prepareImageFileForUpload';

const generateId = () => {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

// ファイルからメディアタイプを判定する関数
const getMediaTypeFromFile = (file) => {
    const mimeType = file.type.toLowerCase();
    const fileName = file.name.toLowerCase();

    // MIMEタイプによる判定
    if (mimeType.startsWith('image/')) {
        return 'image';
    } else if (mimeType.startsWith('video/')) {
        return 'video';
    } else if (mimeType.startsWith('audio/')) {
        return 'audio';
    }

    // ファイル拡張子による判定（フォールバック）
    // バックエンドのvalidators.pyに合わせて統一
    if (fileName.match(/\.(jpg|jpeg|png|gif|heic|heif|tiff)$/)) {
        return 'image';
    } else if (fileName.match(/\.(mp4|mov|m4v|hevc)$/)) {
        return 'video';
    } else if (fileName.match(/\.(m4a|caf|mp3|wav|aac)$/)) {
        return 'audio';
    }

    // デフォルトは画像
    return 'image';
};

const MAX_FILES = 10;

// ファイルサイズ制限（MB）
const MAX_FILE_SIZES = {
    image: 10 * 1024 * 1024,  // 10MB
    video: 50 * 1024 * 1024,  // 50MB
    audio: 20 * 1024 * 1024,  // 20MB
};

export const useMediaListStore = () => {
    const [mediaList, setMediaList] = useState([]);

    const addFiles = async (fileList) => {
        const newFiles = await prepareFileListForMedia(fileList);

        const currentCount = mediaList.length;
        const newCount = newFiles.length;

        if (currentCount + newCount > MAX_FILES) {
            const errorMessage = `ファイル数は最大${MAX_FILES}個までです。現在${currentCount}個のファイルがあります。`;
            throw new Error(errorMessage);
        }

        // ファイルサイズチェック
        for (const file of newFiles) {
            const mediaType = getMediaTypeFromFile(file);
            const maxSize = MAX_FILE_SIZES[mediaType] || MAX_FILE_SIZES.image;

            if (file.size > maxSize) {
                const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
                const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(0);
                const mediaTypeName = mediaType === 'image' ? '画像' : mediaType === 'video' ? '動画' : '音声';
                throw new Error(`${mediaTypeName}ファイルは${maxSizeMB}MB以下にしてください。選択されたファイル: ${fileSizeMB}MB`);
            }
        }

        setMediaList((prev) => {
            const filtered = newFiles.filter(
                (file) =>
                    !prev.some(
                        (p) =>
                            p?.file &&
                            p.file.name === file.name &&
                            p.file.size === file.size &&
                            p.file.type === file.type
                    )
            );

            const wrapped = filtered.map((file) => ({
                id: generateId(),
                file,
                name: '',
                description: '',
                media_type: file.type,
                mediaType: getMediaTypeFromFile(file), // バックエンド用のメディアタイプ
                url: null,
            }));
            return [...prev, ...wrapped];
        });
    }

    const removeFileAt = (index) => {
        setMediaList((prev) => {
            const next = [...prev];
            const removedItem = next[index];

            if (removedItem && removedItem.file && !removedItem.url) {

            }

            next.splice(index, 1);
            return next;
        });
    };

    const updateFileMeta = (index, key, value) => {
        setMediaList((prev) => {
            const next = [...prev];
            next[index][key] = value;
            return next;
        });
    }

    const clearMediaList = () => {
        setMediaList([]);
    };

    return {
        mediaList,
        setMediaList,
        addFiles,
        removeFileAt,
        updateFileMeta,
        clearMediaList,
        maxFiles: MAX_FILES
    };
};
